import { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';

import CheckIcon from '@/assets/check.svg?react';
import cn from '@/utils/cn';

import {
  selectableCardDescriptionStyles,
  selectableCardIndicatorVariants,
  selectableCardPriceStyles,
  selectableCardTitleStyles,
  selectableCardVariants,
} from './selectableCard';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

const CheckboxCard = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(selectableCardVariants({ variant: 'checkbox' }), className)}
    {...props}
  />
));
CheckboxCard.displayName = 'CheckboxCard';

const CheckboxCardHeader = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-2.5 md:contents', className)}
    {...props}
  />
));
CheckboxCardHeader.displayName = 'CheckboxCardHeader';

const CheckboxCardIndicator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      selectableCardIndicatorVariants({ variant: 'checkbox' }),
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator>
      <CheckIcon width={18} className="text-white" />
    </CheckboxPrimitive.Indicator>
  </div>
));
CheckboxCardIndicator.displayName = 'CheckboxCardIndicator';

const CheckboxCardPrice = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<'p'>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('md:order-2', selectableCardPriceStyles, className)}
    {...props}
  />
));
CheckboxCardPrice.displayName = 'CheckboxCardPrice';

const CheckboxCardInfo = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex w-full flex-col gap-1.5 md:order-1 md:flex-1',
      className
    )}
    {...props}
  />
));
CheckboxCardInfo.displayName = 'CheckboxCardInfo';

const CheckboxCardTitle = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<'p'>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(selectableCardTitleStyles, className)}
    {...props}
  />
));
CheckboxCardTitle.displayName = 'CheckboxCardTitle';

const CheckboxCardDescription = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<'p'>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(selectableCardDescriptionStyles, className)}
    {...props}
  />
));
CheckboxCardDescription.displayName = 'CheckboxCardDescription';

export {
  CheckboxCard,
  CheckboxCardHeader,
  CheckboxCardIndicator,
  CheckboxCardPrice,
  CheckboxCardInfo,
  CheckboxCardTitle,
  CheckboxCardDescription,
};
